import axios from 'axios';
import type { PipelineInspectionManifest } from './types/pipelineInspection';
import type {
  PackageConfig,
  PackageDetail,
  PackageEvent,
  PackagePage,
  PackagePageUpdate,
  PackageSummary,
} from './types/packages';
import type {
  WorkspaceConfig,
  WorkspaceEvent,
  WorkspaceRun,
  WorkspaceRunCreate,
  WorkspaceStreamCallbacks,
} from './types/workspace';
import {
  PidDocument,
  AnnotationInfo,
  PnidWizardState,
  PnidStateResponse,
  DraftValidation,
  PnidSetupUpdate,
  PnidEquipment,
  PnidNode,
  PnidNodeLineSegment,
  SegmentsReviewStatus,
  PnidControlLoop,
  PnidControlLoopCreate,
  PnidControlLoopUpdate,
  PnidInstrumentRelationship,
  RelationshipType,
  InstrumentRefType,
  BoundingBox,
  ClickTargetColumn,
  MarkingStatus,
  PnidAIData,
  AIInstrument,
  ExtractionJob,
  ExtractionUploadResponse,
  ExtractionStartResponse,
  BatchExtractionStatus,
  BackgroundExtractionJob,
  CanvasComment,
  CanvasCommentCreate,
  CanvasCommentUpdate,
  IsolationEquipmentOption,
  IsolationPlan,
  IsolationPnidOption,
  PnidInstrument,
  PnidInstrumentResponse,
  PnidInstrumentUpdate,
  LineType,
  InstrumentLineSubtype,
  InstallationType,
  TracingType,
  FlowProfile,
  FlowDirection,
  PrimaryDirection,
  LinkedPfdRef,
  LinkedPnidRef,
  PnidLinkedPfdStreamRef,
  DocumentReviewCorrectionCreate,
  DocumentReviewCorrectionRecord,
  AccountDomains,
  IdentifyAssetRequest,
  IdentifyAssetResponse,
  ReviewPolicyReport,
  ReviewPolicyDoc,
  ReviewPolicyOverview,
  PolicyChange,
  PolicyImpactReport,
  PolicyAgentChatMessage,
  PolicyAgentChatResponse,
  PolicyRuleCounts,
  PolicyDraftEvent,
  PolicyDiffResponse,
  PublicationCandidate,
  PublicationErrorDetail,
  PublishResponse,
  PlantPublicationCandidate,
  PlantPublishPlanResponse,
  PlantPublishResponse,
  LegendSheet,
  LineNumberFormat,
  LineSuggestion,
  RedundancySet,
  AssetDocument,
  PlantAssetMatch,
  PlantRegisterAsset,
  RegisterContext,
  RegisterEquipmentContext,
  TagFormat,
  TagSuggestion,
  TagSuggestionKind,
  PlantHierarchy,
  PlantSummary,
  AgentReviewQueue,
} from './types';
import { normalizeAiData } from './utils/normalizeAiData';
import type { FormSchemaDefinition } from './types/formSchema';
import { TOKEN_KEY, triggerUnauthorized } from './lib/auth';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/pnid_tool',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      triggerUnauthorized();
    }
    return Promise.reject(error);
  },
);

/** The POSTs under a drawing that read it and write nothing. */
const DRAWING_READ_POSTS = ['identify', 'classify-instrument'];
/** The last segment of an element's validate-draft POST, which reads the drawing. */
const VALIDATE_DRAFT_SEGMENT = 'validate-draft';

/** True for a request that writes drawing `docId`: any method but GET on a path under
 *  `/documents/{docId}/pnid`, except the POSTs that only read. */
export function isDrawingWrite(docId: string, method: string | undefined, url: string | undefined): boolean {
  if (!method || method.toLowerCase() === 'get' || !url) return false;
  const prefix = `/documents/${docId}/pnid`;
  const path = url.split('?')[0];
  if (path !== prefix && !path.startsWith(`${prefix}/`)) return false;
  if (path.endsWith(`/${VALIDATE_DRAFT_SEGMENT}`)) return false;
  return !DRAWING_READ_POSTS.some((read) => path === `${prefix}/${read}`);
}

/** Calls `listener` after each successful request that writes drawing `docId`. Returns the
 *  unsubscribe. */
export function onDrawingWrite(docId: string, listener: () => void): () => void {
  const interceptorId = api.interceptors.response.use((response) => {
    if (isDrawingWrite(docId, response.config.method, response.config.url)) listener();
    return response;
  });
  return () => api.interceptors.response.eject(interceptorId);
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pnid_tool';
const PROTECTED_API_PATH_PREFIX = '/pnid_tool';

const toAbsoluteUrl = (url: string): URL => new URL(url, window.location.origin);
const joinApiUrl = (path: string): string => `${API_BASE_URL.replace(/\/$/, '')}${path}`;

export const appendImageCacheVersion = (url: string, version?: string | null): string => {
  const trimmedVersion = version?.trim();
  if (!trimmedVersion) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(trimmedVersion)}`;
};

export const packageApi = {
  getConfig: async (): Promise<PackageConfig> => {
    const response = await api.get('/packages/config');
    return response.data;
  },
  list: async (): Promise<PackageSummary[]> => {
    const response = await api.get('/packages');
    return response.data;
  },
  get: async (packageId: string): Promise<PackageDetail> => {
    const response = await api.get(`/packages/${packageId}`);
    return response.data;
  },
  listEvents: async (packageId: string): Promise<PackageEvent[]> => {
    const response = await api.get(`/packages/${packageId}/events`);
    return response.data;
  },
  upload: async (files: File[], plant: string, acknowledgedRate: string): Promise<PackageDetail> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('plant', plant);
    formData.append('acknowledged_discovery_rate', acknowledgedRate);
    const response = await api.post('/packages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  updatePage: async (
    packageId: string,
    pageId: string,
    update: PackagePageUpdate,
  ): Promise<PackagePage> => {
    const response = await api.patch(`/packages/${packageId}/pages/${pageId}`, update);
    return response.data;
  },
  completeReview: async (packageId: string): Promise<PackageDetail> => {
    const response = await api.post(`/packages/${packageId}/review-complete`);
    return response.data;
  },
  startProcessing: async (
    packageId: string,
    selectedPageIds: string[],
  ): Promise<PackageDetail> => {
    const response = await api.post(`/packages/${packageId}/start-processing`, {
      selected_page_ids: selectedPageIds,
    });
    return response.data;
  },
  fetchSourcePdfBlob: async (packageId: string): Promise<Blob> => {
    const response = await api.get(`/packages/${packageId}/source-pdf`, { responseType: 'blob' });
    return response.data;
  },
};

const WORKSPACE_EVENT_TYPES = new Set([
  'status',
  'text_delta',
  'activity',
  'evidence',
  'artifact_ref',
  'usage',
  'terminal',
]);

const parseWorkspaceStreamFrame = (frame: string): WorkspaceEvent | null => {
  const data = frame
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n');
  if (!data) return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!parsed || typeof parsed !== 'object') return null;
    const candidate = parsed as Partial<WorkspaceEvent>;
    if (
      typeof candidate.run_id !== 'string'
      || typeof candidate.seq !== 'number'
      || !Number.isInteger(candidate.seq)
      // Persisted event sequence numbers start at 1. `after_seq` is exclusive.
      || candidate.seq <= 0
      || typeof candidate.type !== 'string'
      || !WORKSPACE_EVENT_TYPES.has(candidate.type)
      || !candidate.payload
      || typeof candidate.payload !== 'object'
      || Array.isArray(candidate.payload)
      || typeof candidate.created_at !== 'string'
    ) {
      return null;
    }
    return candidate as WorkspaceEvent;
  } catch {
    return null;
  }
};

export const workspaceApi = {
  getConfig: async (): Promise<WorkspaceConfig> => {
    const response = await api.get('/workspace/config');
    return response.data;
  },
  createRun: async (request: WorkspaceRunCreate): Promise<WorkspaceRun> => {
    const response = await api.post('/workspace/runs', request);
    return response.data;
  },
  listRuns: async (): Promise<WorkspaceRun[]> => {
    const response = await api.get('/workspace/runs');
    return Array.isArray(response.data) ? response.data : response.data?.runs ?? [];
  },
  getRun: async (runId: string): Promise<WorkspaceRun> => {
    const response = await api.get(`/workspace/runs/${encodeURIComponent(runId)}`);
    return response.data;
  },
  cancelRun: async (runId: string): Promise<WorkspaceRun> => {
    const response = await api.post(`/workspace/runs/${encodeURIComponent(runId)}/cancel`);
    return response.data;
  },
  streamRun: async (
    runId: string,
    afterSeq: number,
    callbacks: WorkspaceStreamCallbacks,
    signal: AbortSignal,
  ): Promise<void> => {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(
      joinApiUrl(`/workspace/runs/${encodeURIComponent(runId)}/stream?after_seq=${afterSeq}`),
      {
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal,
      },
    );
    if (response.status === 401) {
      triggerUnauthorized();
      const unauthorizedError = new Error('Your session has expired');
      unauthorizedError.name = 'WorkspaceUnauthorizedError';
      throw unauthorizedError;
    }
    if (!response.ok) throw new Error(`Workspace activity could not be loaded (${response.status})`);
    if (!response.body) throw new Error('Workspace activity is unavailable');
    callbacks.onOpen?.();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      frames.forEach((frame) => {
        const event = parseWorkspaceStreamFrame(frame);
        if (event) callbacks.onEvent(event);
      });
      if (done) break;
    }
    const finalEvent = parseWorkspaceStreamFrame(buffer);
    if (finalEvent) callbacks.onEvent(finalEvent);
  },
};

const normalizeProtectedApiUrl = (url: string): string => {
  if (url.startsWith(PROTECTED_API_PATH_PREFIX) && API_BASE_URL !== PROTECTED_API_PATH_PREFIX) {
    return joinApiUrl(url.slice(PROTECTED_API_PATH_PREFIX.length));
  }
  return url;
};

const isProtectedApiUrl = (url: string): boolean => {
  if (url.startsWith(PROTECTED_API_PATH_PREFIX)) {
    return true;
  }
  try {
    const parsedUrl = toAbsoluteUrl(url);
    const parsedBaseUrl = toAbsoluteUrl(API_BASE_URL);
    return parsedUrl.origin === parsedBaseUrl.origin && parsedUrl.pathname.startsWith(parsedBaseUrl.pathname);
  } catch {
    return false;
  }
};

export interface ResolvedImageUrl {
  url: string;
  revoke: boolean;
  originalWidth?: number;
  originalHeight?: number;
  variantWidth?: number;
  variantHeight?: number;
  maxDim?: number;
}

const headerNumber = (headers: Headers, key: string): number | undefined => {
  const value = headers.get(key);
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const fetchProtectedAsset = async (url: string): Promise<Response> => {
  const requestUrl = normalizeProtectedApiUrl(url);
  const token = localStorage.getItem(TOKEN_KEY);
  const response = await fetch(requestUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (response.status === 401) {
    triggerUnauthorized();
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch protected asset (${response.status})`);
  }
  return response;
};

const fetchAuthenticatedObjectUrl = async (url: string): Promise<ResolvedImageUrl> => {
  const response = await fetchProtectedAsset(url);
  return {
    url: window.URL.createObjectURL(await response.blob()),
    revoke: true,
    originalWidth: headerNumber(response.headers, 'X-Pnid-Original-Width'),
    originalHeight: headerNumber(response.headers, 'X-Pnid-Original-Height'),
    variantWidth: headerNumber(response.headers, 'X-Pnid-Variant-Width'),
    variantHeight: headerNumber(response.headers, 'X-Pnid-Variant-Height'),
    maxDim: headerNumber(response.headers, 'X-Pnid-Image-Max-Dim'),
  };
};

/** One resolution level of a drawing's sheet image, with the source size its overlays are drawn in. */
export interface PnidImage {
  blob: Blob;
  originalWidth?: number;
  originalHeight?: number;
}

export const domainsApi = {
  /** All domains, grouped by account. SMEs work across every customer. */
  list: async (): Promise<{ accounts: AccountDomains[] }> => {
    const response = await api.get('/domains');
    return response.data;
  },
};

export const documentApi = {
  upload: async (file: File): Promise<{ doc_id: string; filename: string; image_path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  list: async (filters?: {
    domainId?: string;
    diagramType?: 'pid' | 'pfd';
    q?: string;
  }): Promise<PidDocument[]> => {
    const params = new URLSearchParams();
    if (filters?.domainId) params.append('domain_id', filters.domainId);
    if (filters?.diagramType) params.append('diagram_type', filters.diagramType);
    if (filters?.q) params.append('q', filters.q);
    const query = params.toString();
    const response = await api.get(`/documents${query ? `?${query}` : ''}`);
    return response.data;
  },

  get: async (docId: string): Promise<PidDocument> => {
    const response = await api.get(`/documents/${docId}`);
    return response.data;
  },

  getImageUrl: (docId: string, maxDim?: number): string => {
    const params = new URLSearchParams();
    if (maxDim) params.set('max_dim', String(maxDim));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return joinApiUrl(`/documents/${docId}/image${suffix}`);
  },

  getPnidImage: async (docId: string, maxDim: number): Promise<PnidImage> => {
    const response = await fetchProtectedAsset(documentApi.getImageUrl(docId, maxDim));
    return {
      blob: await response.blob(),
      originalWidth: headerNumber(response.headers, 'X-Pnid-Original-Width'),
      originalHeight: headerNumber(response.headers, 'X-Pnid-Original-Height'),
    };
  },

  resolveImageUrl: async (imageUrl: string): Promise<ResolvedImageUrl> => {
    if (!isProtectedApiUrl(imageUrl)) {
      return { url: imageUrl, revoke: false };
    }
    return fetchAuthenticatedObjectUrl(imageUrl);
  },

  extractStructuredMetadata: async (docId: string): Promise<any> => {
    const response = await api.post(`/documents/${docId}/metadata-structured`);
    return response.data;
  },

  samSegment: async (docId: string, x: number, y: number): Promise<{ bbox: { x1: number; y1: number; x2: number; y2: number }; confidence: number }> => {
    const response = await api.post(`/documents/${docId}/sam-segment`, { x, y });
    return response.data;
  },

  // Annotation-based workflow methods (data from pnid_annotations table)
  listAnnotations: async (user?: string, isAdmin: boolean = false): Promise<AnnotationInfo[]> => {
    const params = new URLSearchParams();
    if (user) params.append('user', user);
    if (isAdmin) params.append('is_admin', 'true');
    const response = await api.get(`/annotations?${params.toString()}`);
    return response.data;
  },

  getAnnotationInfo: async (docId: string): Promise<AnnotationInfo> => {
    const response = await api.get(`/documents/${docId}/annotation-info`);
    return response.data;
  },

  assignDocument: async (docId: string, user: string): Promise<{ success: boolean; doc_id: string; user: string }> => {
    const response = await api.patch(`/documents/${docId}/assign`, { user });
    return response.data;
  },

  updateStatus: async (docId: string, status: string): Promise<{ success: boolean; doc_id: string; status: string }> => {
    const response = await api.patch(`/documents/${docId}/status`, { status });
    return response.data;
  },

  archive: async (docId: string): Promise<{ success: boolean; doc_id: string; deleted: boolean }> => {
    const response = await api.patch(`/documents/${docId}/archive`);
    return response.data;
  },
};

export const pipelineApi = {
  getRunInspection: async (
    runId: string,
    options?: { bucket?: string; pageNum?: number },
  ): Promise<PipelineInspectionManifest> => {
    const params = new URLSearchParams();
    if (options?.bucket) params.set('bucket', options.bucket);
    if (options?.pageNum != null) params.set('page_num', String(options.pageNum));
    const query = params.toString();
    const response = await api.get(`/pipeline/runs/${runId}/inspection${query ? `?${query}` : ''}`);
    return response.data;
  },
};

export const pnidApi = {
  getPublishableAssets: async (
    docId: string,
    signal?: AbortSignal,
  ): Promise<Pick<PnidWizardState, 'equipments' | 'instruments'>> => {
    const response = await api.get(`/documents/${docId}/publishable_assets`, { signal });
    return response.data;
  },

  get: async (docId: string): Promise<PnidStateResponse> => {
    const response = await api.get(`/documents/${docId}/pnid`);
    return response.data;
  },

  /**
   * The boxes an SME can click to mark an instrument on this drawing: the stored
   * generator run, or `null` when the drawing was never generated.
   */
  getClickTargets: async (docId: string): Promise<ClickTargetColumn | null> => {
    const response = await api.get<ClickTargetColumn | null>(
      `/documents/${docId}/pnid/click-targets`,
    );
    return response.data;
  },

  /** What the plant register knows about this drawing's marked equipment. */
  getRegisterContext: async (docId: string): Promise<RegisterContext> => {
    const response = await api.get(`/documents/${docId}/pnid/register-context`);
    return response.data;
  },

  /**
   * What the plant register knows about one tag, resolved in this drawing's
   * scope. Answers for a tag a reviewer has typed but not yet saved, so a tag
   * another drawing owns names that drawing straight away.
   */
  getTagContext: async (
    docId: string,
    options: { tag: string; kind: TagSuggestionKind; signal?: AbortSignal },
  ): Promise<RegisterEquipmentContext> => {
    const params = new URLSearchParams({ tag: options.tag, kind: options.kind });
    const response = await api.get(
      `/documents/${docId}/pnid/tag-context?${params.toString()}`,
      { signal: options.signal },
    );
    return response.data;
  },

  /** Tags the document's plant already uses that match `query`, best match first. */
  getTagSuggestions: async (
    docId: string,
    options: { kind: TagSuggestionKind; query: string; limit?: number; signal?: AbortSignal },
  ): Promise<TagSuggestion[]> => {
    const params = new URLSearchParams({ kind: options.kind, q: options.query });
    if (options.limit != null) params.set('limit', String(options.limit));
    const response = await api.get(
      `/documents/${docId}/pnid/tag-suggestions?${params.toString()}`,
      { signal: options.signal },
    );
    return response.data?.suggestions ?? [];
  },

  /** Line numbers the document's plant already carries that match `query`, best match first. */
  getLineSuggestions: async (
    docId: string,
    query: string,
    limit: number,
    signal: AbortSignal,
  ): Promise<LineSuggestion[]> => {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const response = await api.get<{ suggestions: LineSuggestion[] }>(
      `/documents/${docId}/pnid/line-suggestions?${params.toString()}`,
      { signal },
    );
    return response.data.suggestions;
  },

  exportExcel: async (docId: string): Promise<void> => {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(joinApiUrl(`/documents/${docId}/pnid/export`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.status === 401) {
      triggerUnauthorized();
    }
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = response.headers.get('content-disposition');
    const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `pnid_export_${docId}.xlsx`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  importAgencyExcel: async (docId: string, file: File): Promise<{
    doc_id: string;
    filename: string;
    sheets: string[];
    row_counts: Record<string, number>;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/documents/${docId}/pnid/import-agency-excel`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  mergePrefill: async (docId: string, overwrite: boolean = false): Promise<{
    doc_id: string;
    equipments_created: number;
    nodes_created: number;
    instruments_created: number;
    merge_summary: Record<string, any>;
  }> => {
    const response = await api.post(`/documents/${docId}/pnid/merge-prefill`, null, {
      params: { overwrite },
    });
    const summary = response.data ?? {};
    const counts = summary.counts ?? {};
    return {
      doc_id: summary.doc_id ?? docId,
      equipments_created: counts.equipments ?? 0,
      nodes_created: counts.nodes ?? 0,
      instruments_created: counts.instruments ?? 0,
      merge_summary: summary,
    };
  },

  setPnidSetup: async (docId: string, setup: PnidSetupUpdate): Promise<PnidWizardState> => {
    const response = await api.put(`/documents/${docId}/pnid`, setup);
    return response.data;
  },

  setDiagramType: async (docId: string, diagramType: 'pid' | 'pfd'): Promise<{ id: string; diagram_type: 'pid' | 'pfd' }> => {
    const response = await api.patch(`/documents/${docId}/diagram-type`, { diagram_type: diagramType });
    return response.data;
  },

  importAnnotations: async (
    targetDocId: string,
    sourceDocId: string,
    options?: { overwriteExisting?: boolean },
  ): Promise<{
    source_doc_id: string;
    target_doc_id: string;
    copied: {
      equipments: number;
      nodes: number;
      instruments: number;
      control_loops: number;
      relationships: number;
    };
    replaced: {
      equipments: number;
      nodes: number;
      instruments: number;
      control_loops: number;
      relationships: number;
    };
    overwrote_existing: boolean;
    skipped_relationships: number;
    target_metadata_preserved: {
      doc_id: string;
      pnid_no: string | null;
      pnid_title: string | null;
    };
    geometry_transform: {
      applied: boolean;
      reason: string;
      scale_x: number;
      scale_y: number;
      offset_x: number;
      offset_y: number;
      source_reference?: string | null;
      target_reference?: string | null;
      source_width?: number | null;
      source_height?: number | null;
      target_width?: number | null;
      target_height?: number | null;
    };
  }> => {
    const response = await api.post(`/documents/${targetDocId}/pnid/import-annotations`, {
      source_doc_id: sourceDocId,
      overwrite_existing: options?.overwriteExisting ?? false,
    });
    return response.data;
  },

  shiftAnnotations: async (
    docId: string,
    dx: number,
    dy: number,
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ): Promise<PnidWizardState> => {
    const response = await api.post(`/documents/${docId}/pnid/shift-annotations`, {
      dx,
      dy,
      line_segment_sx: options?.lineSegmentScale?.sx ?? 1,
      line_segment_sy: options?.lineSegmentScale?.sy ?? 1,
    });
    return response.data;
  },

  replaceState: async (
    docId: string,
    state: PnidWizardState,
    options?: { reason?: string },
  ): Promise<PnidWizardState> => {
    const response = await api.put(`/documents/${docId}/pnid/state`, {
      state,
      reason: options?.reason ?? null,
    });
    return response.data;
  },

  shiftSelectedAnnotations: async (
    docId: string,
    targets: { kind: 'equipment' | 'instrument' | 'node' | 'controlLoop' | 'relationship'; id: string }[],
    dx: number,
    dy: number,
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ): Promise<PnidWizardState> => {
    const response = await api.post(`/documents/${docId}/pnid/shift-selected-annotations`, {
      targets,
      dx,
      dy,
      line_segment_sx: options?.lineSegmentScale?.sx ?? 1,
      line_segment_sy: options?.lineSegmentScale?.sy ?? 1,
    });
    return response.data;
  },

  deleteSelectedAnnotations: async (
    docId: string,
    targets: { kind: 'equipment' | 'instrument' | 'node' | 'controlLoop' | 'relationship'; id: string }[],
  ): Promise<PnidWizardState> => {
    const response = await api.post(`/documents/${docId}/pnid/delete-selected-annotations`, {
      targets,
    });
    return response.data;
  },

  reviewItem: async (
    docId: string,
    kind: 'equipments' | 'nodes' | 'instruments',
    objectId: string,
    correction?: DocumentReviewCorrectionCreate,
  ): Promise<PnidEquipment | PnidNode | PnidInstrument> => {
    const response = await api.post(`/documents/${docId}/pnid/${kind}/${objectId}/review`, correction ?? null);
    return response.data;
  },

  listReviewCorrections: async (
    docId: string,
    options?: { targetRef?: string; limit?: number },
  ): Promise<DocumentReviewCorrectionRecord[]> => {
    const params = new URLSearchParams();
    if (options?.targetRef) params.set('target_ref', options.targetRef);
    if (options?.limit) params.set('limit', String(options.limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/documents/${docId}/review-corrections${suffix}`);
    return response.data;
  },

  createReviewCorrection: async (
    docId: string,
    payload: DocumentReviewCorrectionCreate,
  ): Promise<DocumentReviewCorrectionRecord> => {
    const response = await api.post(`/documents/${docId}/review-corrections`, payload);
    return response.data;
  },

  // AI Data endpoints
  getAIData: async (docId: string): Promise<PnidAIData> => {
    try {
      const response = await api.get(`/documents/${docId}/pnid/ai-data`);
      return normalizeAiData(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        const urls = await extractionApi.getDocumentUrls(docId);
        if (urls.ai_data_url) {
          const response = await fetch(urls.ai_data_url);
          if (response.ok) {
            const data = await response.json();
            return normalizeAiData(data);
          }
        }
      }
      throw error;
    }
  },

  checkAIDataExists: async (docId: string): Promise<{ exists: boolean }> => {
    const response = await api.get(`/documents/${docId}/pnid/ai-data/exists`);
    return response.data;
  },

  prefillFromAI: async (docId: string): Promise<PnidWizardState> => {
    const response = await api.post(`/documents/${docId}/pnid/prefill`);
    return response.data;
  },

  getAIInstrumentsForEquipment: async (docId: string, equipmentTag: string): Promise<{ equipment_tag: string; instruments: AIInstrument[] }> => {
    const response = await api.get(`/documents/${docId}/pnid/ai-instruments/${encodeURIComponent(equipmentTag)}`);
    return response.data;
  },

  getAllAIInstruments: async (docId: string): Promise<{ instruments: AIInstrument[] }> => {
    const response = await api.get(`/documents/${docId}/pnid/ai-instruments`);
    return response.data;
  },

  classifyInstrumentCandidates: async (
    docId: string,
    bbox: BoundingBox,
    contextType: 'equipment' | 'node',
    contextId: string,
    topK: number = 3,
  ): Promise<{
    success: boolean;
    context_type: string;
    context_info: string;
    result: {
      candidates: { label: string; score: number }[];
      identified_text?: string;
      symbol_match?: string | null;
      has_tag?: boolean;
      tag_text?: string | null;
      confidence?: string;
      notes?: string;
    };
  }> => {
    const response = await api.post(`/documents/${docId}/pnid/classify-instrument`, {
      bbox,
      context_type: contextType,
      context_id: contextId,
      top_k: topK,
    });
    return response.data;
  },

  createEquipment: async (
    docId: string,
    payload: {
      equipment_no: string;
      equipment_type: string;
      subtype?: string | null;
      description: string;
      pnid_no?: string | null;
      bbox?: BoundingBox | null;
      label_bbox?: BoundingBox | null;
      status?: MarkingStatus;
      details?: Record<string, any>;
      equipment_data?: Record<string, unknown> | null;
      supporting_refs?: Record<string, unknown>[] | null;
      related_docs?: Array<{
        id: string;
        name: string;
        type: string;
        url?: string | null;
        domain_id?: string | null;
      }>;
      ai_instruments?: string[];
      connected_equipments?: Array<{ equipment_id: string; role: string }> | null;
      linked_pnids?: LinkedPnidRef[] | null;
      linked_pfds?: LinkedPfdRef[] | null;
      notes?: string | null;
      painting_spec?: string | null;
      /** Claims or releases this drawing's ownership of the asset's canonical fields. */
      is_primary_source?: boolean | null;
      /** The plant domain that owns the asset. Omitted leaves it to the drawing's own. */
      owner_domain_id?: string;
    }
  ): Promise<PnidEquipment> => {
    const response = await api.post(`/documents/${docId}/pnid/equipments`, payload);
    return response.data;
  },

  updateEquipment: async (
    docId: string,
    equipmentId: string,
    payload: Partial<{
      equipment_no: string;
      equipment_type: string;
      subtype: string | null;
      description: string;
      pnid_no: string | null;
      bbox: BoundingBox | null;
      status: MarkingStatus;
      details: Record<string, any>;
      ai_instruments: string[];
      connected_equipments: Array<{ equipment_id: string; role: string }> | null;
      linked_pnids: LinkedPnidRef[] | null;
      linked_pfds: LinkedPfdRef[] | null;
      notes: string | null;
      painting_spec: string | null;
      /** Claims this drawing as the one that defines the tag, releasing the
       *  plant's other drawings; false gives this drawing's claim up, and null
       *  says nothing. */
      is_primary_source: boolean | null;
      /** The plant domain that owns the asset. Omitted leaves the stored owner alone. */
      owner_domain_id: string;
    }>
  ): Promise<PnidEquipment> => {
    const response = await api.put(`/documents/${docId}/pnid/equipments/${equipmentId}`, payload);
    return response.data;
  },

  deleteEquipment: async (docId: string, equipmentId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/pnid/equipments/${equipmentId}`);
  },

  createNode: async (
    docId: string,
    payload: {
      node_no?: number | null;
      source_ref?: string;
      source_pnid?: string;
      source_id?: string | null;
      to?: string | null;
      dest_ref?: string;
      dest_pnid?: string;
      dest_id?: string | null;
      line_no: string;
      note?: string | null;
      notes?: string | null;
      line_type?: LineType;
      instrument_line_subtype?: InstrumentLineSubtype | null;
      installation_type?: InstallationType | null;
      is_jacketed?: boolean;
      tracing_type?: TracingType | null;
      flow_profile?: FlowProfile | null;
      slope_ratio?: string | null;
      is_two_phase?: boolean;
      flow_direction?: FlowDirection | null;
      primary_direction?: PrimaryDirection | null;
      drain_type?: string | null;
      painting_spec?: string | null;
      is_duct?: boolean;
      duct_bbox?: BoundingBox | null;
      polyline?: number[] | null;
      status?: MarkingStatus;
      line_spec?: Record<string, any>;
      line_segments?: PnidNodeLineSegment[] | null;
      linked_pfd_streams?: PnidLinkedPfdStreamRef[] | null;
      segments_review_status?: SegmentsReviewStatus | null;
    }
  ): Promise<PnidNode> => {
    const response = await api.post(`/documents/${docId}/pnid/nodes`, payload);
    return response.data;
  },

  updateNode: async (
    docId: string,
    nodeId: string,
    payload: Partial<{
      node_no: number;
      source_ref: string;
      source_pnid: string;
      source_id?: string | null;
      to: string | null;
      dest_ref: string;
      dest_pnid: string;
      dest_id?: string | null;
      line_no: string;
      note: string | null;
      notes: string | null;
      line_type: LineType;
      instrument_line_subtype: InstrumentLineSubtype | null;
      installation_type: InstallationType | null;
      is_jacketed: boolean;
      tracing_type: TracingType | null;
      flow_profile: FlowProfile | null;
      slope_ratio: string | null;
      is_two_phase: boolean;
      flow_direction: FlowDirection | null;
      primary_direction: PrimaryDirection | null;
      drain_type: string | null;
      painting_spec: string | null;
      is_duct: boolean;
      duct_bbox: BoundingBox | null;
      polyline: number[] | null;
      status: MarkingStatus;
      line_spec: Record<string, any>;
      line_segments: PnidNodeLineSegment[] | null;
      linked_pfd_streams: PnidLinkedPfdStreamRef[] | null;
      segments_review_status: SegmentsReviewStatus | null;
    }>
  ): Promise<PnidNode> => {
    const response = await api.put(`/documents/${docId}/pnid/nodes/${nodeId}`, payload);
    return response.data;
  },

  deleteNode: async (docId: string, nodeId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/pnid/nodes/${nodeId}`);
  },

  getObjectNotes: async (
    docId: string
  ): Promise<Array<{
    kind: 'equipment' | 'instrument' | 'line';
    object_id: string;
    tag: string | null;
    label: string | null;
    note: string;
  }>> => {
    const response = await api.get(`/documents/${docId}/pnid/object-notes`);
    return response.data;
  },

  autoSequenceNodeInstruments: async (
    docId: string,
    nodeId: string,
    maxDistancePx: number = 500.0
  ): Promise<{
    message: string;
    sequences: Record<string, number>;
    unsequenced_count: number;
    total: number;
  }> => {
    const response = await api.post(
      `/documents/${docId}/pnid/nodes/${nodeId}/auto-sequence`,
      null,
      { params: { max_distance_px: maxDistancePx } }
    );
    return response.data;
  },

  reorderNodeInstruments: async (
    docId: string,
    nodeId: string,
    orderedInstrumentIds: string[]
  ): Promise<{ message: string; sequences: Record<string, number> }> => {
    const response = await api.post(
      `/documents/${docId}/pnid/nodes/${nodeId}/reorder-instruments`,
      { ordered_instrument_ids: orderedInstrumentIds }
    );
    return response.data;
  },

  autoAddBranchInstruments: async (
    docId: string,
    nodeId: string,
    options?: {
      perpendicularThresholdDeg?: number;
      endpointThresholdPx?: number;
      bboxSizePx?: number;
      dryRun?: boolean;
    }
  ): Promise<{
    created?: Array<{
      id: string;
      other_node_no: number;
      other_line_no: string;
      x: number;
      y: number;
      angle_deg: number;
      branch_direction: 'IN' | 'OUT';
    }>;
    candidates?: Array<{
      other_node_no: number;
      other_line_no: string;
      x: number;
      y: number;
      angle_deg: number;
      branch_direction: 'IN' | 'OUT';
      endpoint?: 'start' | 'end';
      distance_px?: number;
    }>;
    skipped_endpoints: Array<{
      other_node_no: number;
      other_line_no: string;
      x: number;
      y: number;
      reason: string;
    }>;
  }> => {
    const response = await api.post(
      `/documents/${docId}/pnid/nodes/${nodeId}/auto-add-branches`,
      null,
      {
        params: {
          perpendicular_threshold_deg: options?.perpendicularThresholdDeg ?? 15.0,
          endpoint_threshold_px: options?.endpointThresholdPx ?? 30.0,
          bbox_size_px: options?.bboxSizePx ?? 30.0,
          dry_run: options?.dryRun ?? false,
        },
      }
    );
    return response.data;
  },

  // === Control Loops ===

  createControlLoop: async (
    docId: string,
    payload: PnidControlLoopCreate
  ): Promise<PnidControlLoop> => {
    const response = await api.post(`/documents/${docId}/pnid/control-loops`, payload);
    return response.data;
  },

  listControlLoops: async (docId: string): Promise<PnidControlLoop[]> => {
    const response = await api.get(`/documents/${docId}/pnid/control-loops`);
    return response.data;
  },

  updateControlLoop: async (
    docId: string,
    loopId: string,
    payload: PnidControlLoopUpdate
  ): Promise<PnidControlLoop> => {
    const response = await api.put(`/documents/${docId}/pnid/control-loops/${loopId}`, payload);
    return response.data;
  },

  deleteControlLoop: async (docId: string, loopId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/pnid/control-loops/${loopId}`);
  },

  // === Instrument Relationships ===

  createRelationship: async (
    docId: string,
    payload: {
      relationship_type: RelationshipType;
      from_type: InstrumentRefType;
      from_id: string;
      from_ref: string;
      to_type: InstrumentRefType;
      to_id: string;
      to_ref: string;
      auto_generated?: boolean;
    }
  ): Promise<PnidInstrumentRelationship> => {
    const response = await api.post(`/documents/${docId}/pnid/relationships`, payload);
    return response.data;
  },

  listRelationships: async (
    docId: string,
    filters?: {
      from_ref?: string;
      to_ref?: string;
      relationship_type?: RelationshipType;
      auto_generated?: boolean;
    }
  ): Promise<PnidInstrumentRelationship[]> => {
    const params = new URLSearchParams();
    if (filters?.from_ref) params.append('from_ref', filters.from_ref);
    if (filters?.to_ref) params.append('to_ref', filters.to_ref);
    if (filters?.relationship_type) params.append('relationship_type', filters.relationship_type);
    if (filters?.auto_generated !== undefined) params.append('auto_generated', String(filters.auto_generated));
    const response = await api.get(`/documents/${docId}/pnid/relationships?${params.toString()}`);
    return response.data;
  },

  getRelationship: async (docId: string, relationshipId: string): Promise<PnidInstrumentRelationship> => {
    const response = await api.get(`/documents/${docId}/pnid/relationships/${relationshipId}`);
    return response.data;
  },

  deleteRelationship: async (docId: string, relationshipId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/pnid/relationships/${relationshipId}`);
  },

  getRelationshipsForInstrument: async (
    docId: string,
    instrumentRef: string
  ): Promise<{ instrument_ref: string; relationships: PnidInstrumentRelationship[] }> => {
    const response = await api.get(`/documents/${docId}/pnid/instrument-relationships/${encodeURIComponent(instrumentRef)}`);
    return response.data;
  },

  // === Version History ===

  getVersionHistory: async (docId: string): Promise<{ versions: VersionInfo[]; count: number }> => {
    const response = await api.get(`/documents/${docId}/pnid/versions`);
    return response.data;
  },

  getVersion: async (docId: string, versionNumber: number): Promise<VersionData> => {
    const response = await api.get(`/documents/${docId}/pnid/versions/${versionNumber}`);
    return response.data;
  },

  restoreVersion: async (docId: string, versionNumber: number, user?: string): Promise<PnidWizardState> => {
    const params = user ? `?user=${encodeURIComponent(user)}` : '';
    const response = await api.post(`/documents/${docId}/pnid/versions/${versionNumber}/restore${params}`);
    return response.data;
  },
};

// === Unified Instrument API ===
// Uses the consolidated /instruments endpoints (backend routes.py:537-593)

export const instrumentApi = {
  getInstrument: async (docId: string, instrumentId: string): Promise<PnidInstrumentResponse> => {
    const response = await api.get(`/documents/${docId}/pnid/instruments/${instrumentId}`);
    return response.data;
  },

  listInstruments: async (
    docId: string,
    filters?: { context_type?: string; context_id?: string; provenance_source?: string }
  ): Promise<PnidInstrument[]> => {
    const params = new URLSearchParams();
    if (filters?.context_type) params.append('context_type', filters.context_type);
    if (filters?.context_id) params.append('context_id', filters.context_id);
    if (filters?.provenance_source) params.append('provenance_source', filters.provenance_source);
    const query = params.toString();
    const response = await api.get(`/documents/${docId}/pnid/instruments${query ? `?${query}` : ''}`);
    return response.data;
  },

  updateInstrument: async (docId: string, instrumentId: string, data: PnidInstrumentUpdate): Promise<PnidInstrument> => {
    const response = await api.put(`/documents/${docId}/pnid/instruments/${instrumentId}`, data);
    return response.data;
  },

  createInstrument: async (docId: string, data: Record<string, any>): Promise<PnidInstrument> => {
    const response = await api.post(`/documents/${docId}/pnid/instruments`, data);
    return response.data;
  },

  deleteInstrument: async (docId: string, instrumentId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/pnid/instruments/${instrumentId}`);
  },
};

// === Asset Identification API ===
// Draw-time pre-fill for the mark dialog (backend POST /documents/{docId}/pnid/identify).

/**
 * The dialog opens either way, so a slow suggestion is worth less to the SME than
 * an empty form now. This budget is local to identify; the shared instance keeps
 * its own default for every other call.
 *
 * A large-format sheet costs about 1.5 s to identify, which a tighter budget cuts
 * off mid-answer, so this one sits well clear of that cost rather than beside it.
 */
export const IDENTIFY_TIMEOUT_MS = 5000;

export const assetApi = {
  identify: async (
    docId: string,
    body: IdentifyAssetRequest,
    options?: { signal?: AbortSignal },
  ): Promise<IdentifyAssetResponse> => {
    const response = await api.post(`/documents/${docId}/pnid/identify`, body, {
      timeout: IDENTIFY_TIMEOUT_MS,
      signal: options?.signal,
    });
    return response.data;
  },

  // Each validate-draft route takes the body the element's Save sends, applies it to a copy of
  // the drawing, and writes nothing. A duplicate tag answers 409 as the instrument PUT does.
  validateDraftInstrument: async (
    docId: string,
    instrumentId: string,
    body: PnidInstrumentUpdate,
  ): Promise<DraftValidation> => {
    const response = await api.post(`/documents/${docId}/pnid/instruments/${instrumentId}/${VALIDATE_DRAFT_SEGMENT}`, body);
    return response.data;
  },

  /** Answers 400 when the body's duct flag and geometry conflict. */
  validateDraftNode: async (
    docId: string,
    nodeId: string,
    body: Parameters<typeof pnidApi.updateNode>[2],
  ): Promise<DraftValidation> => {
    const response = await api.post(`/documents/${docId}/pnid/nodes/${nodeId}/${VALIDATE_DRAFT_SEGMENT}`, body);
    return response.data;
  },

  validateDraftEquipment: async (
    docId: string,
    equipmentId: string,
    body: Parameters<typeof pnidApi.updateEquipment>[2],
  ): Promise<DraftValidation> => {
    const response = await api.post(`/documents/${docId}/pnid/equipments/${equipmentId}/${VALIDATE_DRAFT_SEGMENT}`, body);
    return response.data;
  },
};

export const isolationApi = {
  listPnids: async (): Promise<IsolationPnidOption[]> => {
    const response = await api.get('/isolation/pnids');
    return response.data;
  },

  listEquipments: async (pnidNo: string): Promise<IsolationEquipmentOption[]> => {
    const response = await api.get(`/isolation/pnids/${encodeURIComponent(pnidNo)}/equipments`);
    return response.data;
  },

  generatePlan: async (pnidNo: string, equipmentNo: string): Promise<IsolationPlan> => {
    const response = await api.post('/isolation/', {
      pnid_no: pnidNo,
      equipment_no: equipmentNo,
    });
    return response.data;
  },
};

// Version history types
export interface VersionInfo {
  id: number;
  annotation_id: string;
  diagram_type?: 'pid' | 'pfd';
  version_number: number;
  created_at: string;
  created_by: string | null;
}

export interface VersionData extends VersionInfo {
  data: PnidWizardState;
}

// === P&ID AI Extraction API ===

export interface DocumentUrls {
  doc_id: string;
  pdf_url: string | null;
  image_url: string | null;
  ai_data_url: string | null;
  excel_url: string | null;
}

export interface LegendEntry {
  section: string;
  name: string;
  notes: string;
}

export interface LegendData {
  symbols: LegendEntry[];
  categories: Record<string, string[]>;
  legend_json?: any;
}

export const extractionApi = {
  /**
   * Upload a PDF file. Does NOT start extraction by default.
   * Pass startExtraction=true to immediately begin AI extraction.
   */
  upload: async (file: File, startExtraction: boolean = false): Promise<ExtractionUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/pnid-ai/upload?start_extraction=${startExtraction}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Manually start AI extraction for an existing document.
   */
  startForDocument: async (docId: string): Promise<ExtractionUploadResponse> => {
    const response = await api.post(`/pnid-ai/start/${docId}`);
    return response.data;
  },

  /**
   * Start extraction for existing documents.
   * Uses background mode for 1 file, batch API for multiple.
   */
  startExtraction: async (docIds: string[]): Promise<ExtractionStartResponse> => {
    const response = await api.post('/pnid-ai/extract', { doc_ids: docIds });
    return response.data;
  },

  /**
   * Get all S3 URLs for a document (PDF, image, AI data, Excel).
   */
  getDocumentUrls: async (docId: string): Promise<DocumentUrls> => {
    const response = await api.get(`/documents/${docId}/urls`);
    return response.data;
  },

  /**
   * Get the global P&ID legend symbols.
   */
  getLegend: async (): Promise<LegendData> => {
    const response = await api.get('/pnid-ai/legend');
    return response.data;
  },

  /**
   * Upload AI extraction JSON for a document.
   */
  uploadAiData: async (docId: string, file: File): Promise<{ doc_id: string; ai_data_url: string; path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/documents/${docId}/pnid/ai-data/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Get status of an extraction job.
   * Poll this until status is 'completed' or 'failed'.
   */
  getStatus: async (jobId: string): Promise<ExtractionJob | BatchExtractionStatus> => {
    const response = await api.get(`/pnid-ai/status/${jobId}`);
    return response.data;
  },

  /**
   * Cancel a running extraction job.
   */
  cancel: async (jobId: string): Promise<{ job_id: string; status: string; message: string }> => {
    const response = await api.post(`/pnid-ai/cancel/${jobId}`);
    return response.data;
  },

  /**
   * List active background extraction jobs.
   */
  listBackgroundJobs: async (): Promise<{ jobs: BackgroundExtractionJob[]; count: number }> => {
    const response = await api.get('/pnid-ai/background-jobs');
    return response.data;
  },

  /**
   * Poll and update all active background extraction jobs.
   */
  pollBackgroundJobs: async (): Promise<{ jobs: BackgroundExtractionJob[]; count: number }> => {
    const response = await api.post('/pnid-ai/background-jobs/poll');
    return response.data;
  },

  /**
   * Poll extraction status until complete.
   * Returns the final job status.
   */
  pollUntilComplete: async (
    jobId: string,
    onProgress?: (status: ExtractionJob | BatchExtractionStatus) => void,
    intervalMs: number = 60000,
    maxAttempts: number = 100
  ): Promise<ExtractionJob | BatchExtractionStatus> => {
    let attempts = 0;
    let lastStatus: ExtractionJob | BatchExtractionStatus | null = null;
    
    while (attempts < maxAttempts) {
      const status = await extractionApi.getStatus(jobId);
      lastStatus = status;
      
      if (onProgress) {
        onProgress(status);
      }
      
      // Check if job is complete
      const jobStatus = 'status' in status ? status.status : '';
      if (jobStatus === 'completed' || jobStatus === 'failed') {
        return status;
      }
      
      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    }
    
    if (lastStatus) {
      return { ...lastStatus, timed_out: true };
    }
    throw new Error(`Extraction job ${jobId} timed out after ${maxAttempts} attempts`);
  },
};

// === Canvas Comments API ===

export const commentsApi = {
  /**
   * List all comments for a document.
   */
  list: async (docId: string, includeResolved: boolean = true): Promise<CanvasComment[]> => {
    const params = new URLSearchParams();
    if (!includeResolved) params.append('include_resolved', 'false');
    const response = await api.get(`/documents/${docId}/comments?${params.toString()}`);
    return response.data;
  },

  /**
   * Create a new comment.
   */
  create: async (docId: string, payload: CanvasCommentCreate): Promise<CanvasComment> => {
    const response = await api.post(`/documents/${docId}/comments`, payload);
    return response.data;
  },

  /**
   * Get a specific comment by ID.
   */
  get: async (docId: string, commentId: string): Promise<CanvasComment> => {
    const response = await api.get(`/documents/${docId}/comments/${commentId}`);
    return response.data;
  },

  /**
   * Update a comment's text.
   */
  update: async (docId: string, commentId: string, payload: CanvasCommentUpdate): Promise<CanvasComment> => {
    const response = await api.put(`/documents/${docId}/comments/${commentId}`, payload);
    return response.data;
  },

  /**
   * Resolve a comment.
   */
  resolve: async (docId: string, commentId: string, resolvedBy: string): Promise<CanvasComment> => {
    const response = await api.post(`/documents/${docId}/comments/${commentId}/resolve`, { resolved_by: resolvedBy });
    return response.data;
  },

  /**
   * Unresolve a comment.
   */
  unresolve: async (docId: string, commentId: string): Promise<CanvasComment> => {
    const response = await api.post(`/documents/${docId}/comments/${commentId}/unresolve`);
    return response.data;
  },

  /**
   * Delete a comment.
   */
  delete: async (docId: string, commentId: string): Promise<void> => {
    await api.delete(`/documents/${docId}/comments/${commentId}`);
  },
};

// ─── Analytics (Dashboard) ───

import type { DashboardOverview, UserStatsRow } from './types/dashboard';

export const analyticsApi = {
  getOverview: async (): Promise<DashboardOverview> => {
    const response = await api.get('/analytics/overview');
    return response.data;
  },
  getUserStats: async (days: number = 7): Promise<UserStatsRow[]> => {
    const response = await api.get('/analytics/tables/user-stats', { params: { days } });
    return response.data;
  },
};

import type { AdminDashboardOverview } from './types/adminDashboard';

export const adminDashboardApi = {
  getOverview: async (): Promise<AdminDashboardOverview> => {
    const response = await api.get('/admin-dashboard/overview');
    return response.data;
  },
};

// Review policy (pnid_review_policy.md) — live findings for a document or a
// single object. Findings carry target_kind/target_id for canvas badging.
export const reviewPolicyApi = {
  getReport: async (
    docId: string,
    opts?: {
      signoff?: boolean;
      policyVersion?: string;
    },
  ): Promise<ReviewPolicyReport> => {
    const params: Record<string, string | boolean> = {};
    if (opts?.signoff) params.signoff = true;
    if (opts?.policyVersion) params.policy_version = opts.policyVersion;
    const response = await api.get(`/documents/${docId}/pnid/review-policy`, { params });
    return response.data;
  },
};

// Policy administration — SME-editable, DB-versioned review policy.
export const policyAdminApi = {
  getOverview: async (): Promise<ReviewPolicyOverview> => {
    const response = await api.get('/review-policy');
    return response.data;
  },

  saveDraft: async (
    policy: ReviewPolicyDoc,
    changeSummary?: string,
    source: 'manual' | 'agent' | 'restore' = 'manual',
  ): Promise<{ draft: ReviewPolicyDoc; changes: PolicyChange[]; events: PolicyDraftEvent[] }> => {
    const response = await api.put('/review-policy/draft', {
      policy,
      change_summary: changeSummary,
      source,
    });
    return response.data;
  },

  getDiff: async (base: string, compare: string): Promise<PolicyDiffResponse> => {
    const response = await api.get('/review-policy/diff', { params: { base, compare } });
    return response.data;
  },

  discardDraft: async (): Promise<{ discarded: boolean }> => {
    const response = await api.delete('/review-policy/draft');
    return response.data;
  },

  publish: async (): Promise<{ published: ReviewPolicyDoc }> => {
    const response = await api.post('/review-policy/draft/publish');
    return response.data;
  },

  restoreVersion: async (versionNo: number): Promise<{ draft: ReviewPolicyDoc }> => {
    const response = await api.post(`/review-policy/versions/${versionNo}/restore`);
    return response.data;
  },

  getRuleCounts: async (domainId: string): Promise<PolicyRuleCounts> => {
    const response = await api.get('/review-policy/rule-counts', { params: { domain_id: domainId } });
    return response.data;
  },

  getImpact: async (
    domainId: string,
    opts?: { includeAi?: boolean; signoff?: boolean },
  ): Promise<PolicyImpactReport> => {
    const params: Record<string, string | boolean> = { domain_id: domainId };
    if (opts?.includeAi) params.include_ai = true;
    if (opts?.signoff) params.signoff = true;
    const response = await api.get('/review-policy/impact', { params });
    return response.data;
  },

  agentChat: async (messages: PolicyAgentChatMessage[]): Promise<PolicyAgentChatResponse> => {
    const response = await api.post('/review-policy/agent/chat', { messages });
    return response.data;
  },
};

export interface EquipmentTypeOption {
  key: string;
  label: string;
  /** The sub-type labels a reviewer may pick under this type, in the order the
   *  vocabulary lists them. Empty when the type has none. */
  subtypes: string[];
}

export const equipmentTypesApi = {
  /** The reviewed equipment taxonomy, served from copilot via the backend. */
  list: async (): Promise<EquipmentTypeOption[]> => {
    const response = await api.get<{ types: EquipmentTypeOption[] }>('/equipment-types');
    return response.data.types ?? [];
  },
};

export const formSchemasApi = {
  list: async (): Promise<FormSchemaDefinition[]> => {
    const response = await api.get<FormSchemaDefinition[]>('/form-schemas');
    return response.data;
  },

  get: async (formKind: string): Promise<FormSchemaDefinition> => {
    const response = await api.get<FormSchemaDefinition>(`/form-schemas/${formKind}`);
    return response.data;
  },

  save: async (
    formKind: string,
    schema: FormSchemaDefinition,
  ): Promise<FormSchemaDefinition> => {
    const response = await api.put<FormSchemaDefinition>(`/form-schemas/${formKind}`, {
      sections: schema.sections,
    });
    return response.data;
  },
};
/** A plant's redundancy sets. Every write answers with the plant's full list. */
export const redundancySetApi = {
  list: async (domainId: string): Promise<RedundancySet[]> => {
    const response = await api.get(`/plants/${encodeURIComponent(domainId)}/redundancy-sets`);
    return response.data;
  },

  /** Puts the tags in one set: a new set, or the one set some of them already share. */
  putTags: async (domainId: string, tags: string[]): Promise<RedundancySet[]> => {
    const response = await api.put(
      `/plants/${encodeURIComponent(domainId)}/redundancy-sets`,
      { tags },
    );
    return response.data;
  },

  removeMember: async (domainId: string, tag: string): Promise<RedundancySet[]> => {
    const response = await api.delete(
      `/plants/${encodeURIComponent(domainId)}/redundancy-sets/members/${encodeURIComponent(tag)}`,
    );
    return response.data;
  },
};

/** The plant register's assets, and the documents linked to each. */
export const plantAssetApi = {
  /** The register asset that carries `tag` in the plant. Two rows are asked for, which
   *  is enough to tell a tag that names one asset from a tag that names several. */
  findPlantAsset: async (domainId: string, tag: string): Promise<PlantAssetMatch> => {
    const response = await api.get<{ assets: PlantRegisterAsset[] }>('/plant-assets', {
      params: { domain_id: domainId, tag, limit: 2 },
    });
    const assets = response.data.assets;
    if (assets.length === 0) return { match: 'none' };
    if (assets.length === 1) return { match: 'one', asset: assets[0] };
    return { match: 'several' };
  },

  fetchAssetDocuments: async (assetId: string): Promise<AssetDocument[]> => {
    const response = await api.get<{ documents: AssetDocument[] }>(
      `/plant-assets/${encodeURIComponent(assetId)}/documents`,
    );
    return response.data.documents;
  },
};

/** A plant's legend sheets. Every write answers with the plant's full list. */
export const legendSheetApi = {
  list: async (domainId: string): Promise<LegendSheet[]> => {
    const response = await api.get(`/plants/${encodeURIComponent(domainId)}/legend-sheets`);
    return response.data;
  },

  /** Uploads the sheet's files and creates the sheet in one request. */
  create: async (
    domainId: string,
    sheet: { name: string; files: File[] },
  ): Promise<LegendSheet[]> => {
    const formData = new FormData();
    formData.append('name', sheet.name);
    sheet.files.forEach((file) => formData.append('files', file));
    const response = await api.post(
      `/plants/${encodeURIComponent(domainId)}/legend-sheets`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  update: async (
    domainId: string,
    sheetId: string,
    update: {
      name: string;
      line_number_format: LineNumberFormat | null;
      tag_format: TagFormat | null;
    },
  ): Promise<LegendSheet[]> => {
    const response = await api.put(
      `/plants/${encodeURIComponent(domainId)}/legend-sheets/${encodeURIComponent(sheetId)}`,
      update,
    );
    return response.data;
  },

  remove: async (domainId: string, sheetId: string): Promise<LegendSheet[]> => {
    const response = await api.delete(
      `/plants/${encodeURIComponent(domainId)}/legend-sheets/${encodeURIComponent(sheetId)}`,
    );
    return response.data;
  },
};

/** The plant-wide equipment view: which domains exist, and one domain's whole
 *  asset list. The `/plants/...` routes are addressed by `domain_id`. */
export const plantApi = {
  listPlants: async (): Promise<PlantSummary[]> => {
    const response = await api.get<{ plants: PlantSummary[] }>('/plants');
    return response.data.plants ?? [];
  },

  getHierarchy: async (domainId: string): Promise<PlantHierarchy> => {
    const response = await api.get(`/plants/${encodeURIComponent(domainId)}/hierarchy`);
    return response.data;
  },

  /** The agent's pending proposals on the plant's drawings; `docId` narrows to one drawing. */
  getAgentReviewQueue: async (domainId: string, docId?: string | null): Promise<AgentReviewQueue> => {
    const response = await api.get<AgentReviewQueue>(
      `/plants/${encodeURIComponent(domainId)}/agent-review-queue`,
      { params: docId ? { doc_id: docId } : undefined },
    );
    return response.data;
  },
};

export const plantPublicationApi = {
  getCandidate: async (domainId: string): Promise<PlantPublicationCandidate> => {
    const response = await api.get(
      `/plants/${encodeURIComponent(domainId)}/pnid/publication-candidate`,
    );
    return response.data;
  },

  getPublishPlan: async (domainId: string): Promise<PlantPublishPlanResponse> => {
    const response = await api.get(
      `/plants/${encodeURIComponent(domainId)}/pnid/publish-plan`,
    );
    return response.data;
  },

  publish: async (
    domainId: string,
    body: {
      drawings: Array<{
        doc_id: string;
        expected_updated_at: string;
        expected_data_sha256: string;
      }>;
    },
  ): Promise<PlantPublishResponse> => {
    const response = await api.post(
      `/plants/${encodeURIComponent(domainId)}/pnid/publish`,
      body,
    );
    return response.data;
  },
};

/**
 * Publication failures answer with an object `detail` carrying a `code`; rendering
 * `detail` as a string would print `[object Object]`, so callers branch on the code
 * read through here and fall back to their own message when the body is absent.
 */
export const readPublicationErrorDetail = (error: unknown): PublicationErrorDetail | null => {
  if (!axios.isAxiosError(error)) return null;
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as PublicationErrorDetail;
  }
  return null;
};

// Both calls require edit permission: the same user may read the candidate and
// POST the publish.
export const publicationApi = {
  getCandidate: async (docId: string): Promise<PublicationCandidate> => {
    const response = await api.get(`/documents/${docId}/pnid/publication-candidate`);
    return response.data;
  },

  publish: async (
    docId: string,
    body: { expected_updated_at: string; expected_data_sha256: string },
  ): Promise<PublishResponse> => {
    const response = await api.post(`/documents/${docId}/pnid/publish`, body);
    return response.data;
  },
};

export default api;
